import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { select, Store } from '@ngrx/store';
import { AppState } from '@root/app-state.module';
import { COLUMN_DEFS, DATA_ELEMENTS_FOR_EXPORT } from '@root/reports/shared/constants/postcards-modal.constants';
import { GridModule } from '@root/search/components/grid/grid.module';
import { SharedModule } from '@shared/shared.module';
import { ADDRESS_TYPE, FONT_CASE } from '@store/labels/labels.model';
import { GetGridExportInfo, StartPostcardsExport } from '@store/search-board/search-board.actions';
import { ExportGridInfo, PropertyData, PropertyIdentifier, PropertyInformation, Range, SidePanelTab, SidePanelView } from '@store/search-board/search-board.model';
import { selectExportGridInfo, selectSidePanelState } from '@store/search-board/search-board.selector';
import { ColDef, GridApi, GridOptions, GridReadyEvent } from 'ag-grid-community';
import { Observable, tap } from 'rxjs';
import { PaginationComponent } from '../pagination/pagination.component';
import { SearchMixpanelUtil } from '@root/search/search-mixpanel-util';
import { MixpanelService } from '@shared/services/mixpanel.service';

@UntilDestroy()
@Component({
  selector: 'rlst-postcards-modal',
  standalone: true,
  imports: [
    CommonModule,
    SharedModule,
    ReactiveFormsModule,
    GridModule,
    PaginationComponent,
  ],
  templateUrl: './postcards-modal.component.html',
  styleUrls: ['./postcards-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PostcardsModalComponent implements OnInit {
  public form: FormGroup;
  public FONT_CASE: typeof FONT_CASE = FONT_CASE;
  public ADDRESS_TYPE: typeof ADDRESS_TYPE = ADDRESS_TYPE;
  public isExportRangeValid: boolean = true;
  public rowData: Array<PropertyData>;
  public exportInfo$: Observable<ExportGridInfo>;
  public gridOptions: GridOptions;
  public columnDefs: Array<ColDef> = COLUMN_DEFS[ADDRESS_TYPE.TAXBILLING];
  private range: Range;
  public gridApi: GridApi;
  private availableLimit: number = 0;
  private propertyIdentifiers: Array<PropertyIdentifier>;
  private readonly searchMixpanelUtil: SearchMixpanelUtil = new SearchMixpanelUtil(this.store$);
  private sidePanelTabOpened: SidePanelTab;

  constructor(
    private readonly dialogRef: MatDialogRef<PostcardsModalComponent>,
    private readonly fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public readonly data: { propertyInformation: Array<PropertyInformation> },
    private readonly store$: Store<AppState>,
    private readonly mixpanelService: MixpanelService,
  ) { }

  public ngOnInit(): void {
    this.gridOptions = {
      theme: 'legacy',
      pagination: true,
      paginationPageSize: 6,
      suppressPaginationPanel: true,
    };
    this.store$.dispatch(new GetGridExportInfo());
    this.exportInfo$ = this.store$.pipe(
      select(selectExportGridInfo),
      tap((info: ExportGridInfo) => this.availableLimit = info?.availableLimit),
    );
    this.form = this.fb.group({
      fontCase: FONT_CASE.MIXED_CASE,
      addressType: ADDRESS_TYPE.TAXBILLING,
      duplicateLabelsAllowed: false,
    });

    this.rowData = this.data.propertyInformation.map(
      (propertyInformation: PropertyInformation, idx: number) => ({
        ...propertyInformation.propertyData,
        INDEX: idx + 1,
      }));

    this.propertyIdentifiers = this.data.propertyInformation.map(
      (propertyInformation: PropertyInformation) => propertyInformation.propertyIdentifier
    );

    // this.exportService.postCardPropertyDetails({
    //   propertyIdentifiers: this.propertyIdentifiers,
    //   dataElements: [...DATA_ELEMENTS_FOR_EXPORT[ADDRESS_TYPE.TAXBILLING], ...DATA_ELEMENTS_FOR_EXPORT[ADDRESS_TYPE.ADDRESS]],
    // }).pipe(
    //   untilDestroyed(this),
    // ).subscribe((propertyData: Array<PropertyData>) => {
    //   this.rowData = propertyData;
    //   this.cd.detectChanges();
    // })

    this.form.controls.addressType.valueChanges.pipe(
      untilDestroyed(this),
    ).subscribe((addressType: string) => {
      this.gridApi.setGridOption('columnDefs', COLUMN_DEFS[addressType]);
    });

    this.store$
      .pipe(
        select(selectSidePanelState),
        untilDestroyed(this)
      )
      .subscribe((sidePanelView: SidePanelView) => {
        this.sidePanelTabOpened = sidePanelView.sidePanelTabOpened;
        this.searchMixpanelUtil.setupMixPanelItems(this.sidePanelTabOpened);
        this.mixpanelService.track('Send Postcards - Send Postcards Click', {
          currentPageName: this.searchMixpanelUtil.currentPageName,
          searchType: this.sidePanelTabOpened
        });
      });
  }

  public onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
  }

  public setRange(range: Range | null): void {
    this.range = range;
  }

  public setExportRangeValidity(isValid: boolean): void {
    this.isExportRangeValid = isValid;
  }

  public isLimitExceeded(): boolean {
    return !this.availableLimit ||
      (this.range && (this.availableLimit < (this.range.to - this.range.from + 1)));
  }

  public close(isCreate?: boolean): void {
    if (!isCreate) {
      this.mixpanelService.track('Send Postcards - Send Postcards Cancel', {
        currentPageName: this.searchMixpanelUtil.currentPageName,
        searchType: this.sidePanelTabOpened,
      });
    }
    this.dialogRef.close();
  }

  public create(): void {
    if(this.range?.from || this.range?.to) {
      this.propertyIdentifiers = this.propertyIdentifiers.slice(this.range.from - 1, this.range.to)
    }
    this.store$.dispatch(new StartPostcardsExport({
        useCapitalLetters: this.form.value.fontCase === FONT_CASE.CAPITAL_CASE,
        noDuplicates: this.form.value.duplicateLabelsAllowed,
        dataElements: DATA_ELEMENTS_FOR_EXPORT[this.form.value.addressType],
        propertyIdentifiers: this.propertyIdentifiers,
      }
    ));
    this.mixpanelService.track('Send Postcards - Send Postcards Finalize', {
      currentPageName: this.searchMixpanelUtil.currentPageName,
      searchType: this.sidePanelTabOpened,
      recordsExported: this.propertyIdentifiers.length,
      exportsRemaining: this.availableLimit - this.propertyIdentifiers.length,
      letterCase: this.form.value.fontCase,
      addressType: this.form.value.addressType,
      duplicatesAllowed: this.form.value.duplicateLabelsAllowed,
      exportOptions: (!this.range?.from && !this.range?.to) ?  'Use all remaining to export' : 'Export range',
    });
    this.close(true);
  }
}
